import type {
  AssignChannelInput,
  TienNetworkKy,
  CapNhatHopDongInput,
  HopDongDaXoa,
  KhoiPhucHopDongInput,
  KhoiPhucHopDongKetQua,
  NhatKyHopDong,
  TaoPhienBanHopDongInput,
  TinhHinhHopDong,
  XemTruocXoaHopDong,
  XemTruocXoaHopDongInput,
  XoaHopDongInput,
  UpdateChannelLinkInput,
  ChannelPartnerLink,
  ConfirmStatementInput,
  ContractInput,
  CreatePartnerInput,
  CreateTicketInput,
  DoiTacDanhBa,
  HopDongDanhBa,
  NetworksSummary,
  Partner,
  PartnerCard,
  PartnerChannelRow,
  PartnerContract,
  PartnerTicket,
  PreviewStatementInput,
  Statement,
  StatementImportTemplate,
  StatementLine,
  StatementPreview,
  StatementStatus,
  StatementTemplateInput,
  TicketStatus,
  UpdatePartnerInput,
  UploadFileResult,
} from '@qlk/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, goiApi, type LoiApi } from '../../lib/api.js';

const GOC = '/api/v1/networks';

export const KHOA = {
  doiTac: (period?: string) => ['networks', 'partners', period ?? ''] as const,
  motDoiTac: (id: string) => ['networks', 'partner', id] as const,
  tomTat: ['networks', 'summary'] as const,
  tienKy: (ky: string) => ['networks', 'tien-ky', ky] as const,
  kenh: (period?: string) => ['networks', 'channels', period ?? ''] as const,
  kenhCuaDoiTac: (id: string, period?: string) =>
    ['networks', 'partner-channels', id, period ?? ''] as const,
  lichSuKenh: (channelId: string) => ['networks', 'channel-history', channelId] as const,
  hopDong: (id: string) => ['networks', 'contracts', id] as const,
  danhBa: ['networks', 'danh-ba'] as const,
  hopDongDanhBa: (id: string) => ['networks', 'danh-ba', 'contracts', id] as const,
  template: (id: string) => ['networks', 'templates', id] as const,
  saoKe: (loc: LocSaoKe) => ['networks', 'statements', loc] as const,
  motSaoKe: (id: string) => ['networks', 'statement', id] as const,
  ticket: (status?: TicketStatus) => ['networks', 'tickets', status ?? ''] as const,
};

export interface LocSaoKe {
  partnerId?: string;
  period?: string;
  status?: StatementStatus;
}

function thamSo(obj: Record<string, string | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(obj)) if (v) sp.set(k, v);
  const chuoi = sp.toString();
  return chuoi ? `?${chuoi}` : '';
}

/* -------------------------------- Đọc -------------------------------- */

/** `bat = false` cho người không có quyền `networks:view` (màn Quản lý kênh dùng). */
export function useDoiTac(period?: string, bat = true) {
  return useQuery<PartnerCard[], LoiApi>({
    queryKey: KHOA.doiTac(period),
    queryFn: () => api.get<PartnerCard[]>(`${GOC}${thamSo({ period })}`),
    enabled: bat,
  });
}

/**
 * Tiền của **đúng kỳ đang chọn** cho trang Network: ba ô chỉ số, số trên từng
 * thẻ đối tác và từng dòng kênh.
 *
 * Một truy vấn cho cả ba chỗ — trước đây ba chỗ đọc ba nguồn khác nhau (sao kê
 * của kỳ gần nhất, tạm tính của tháng đang chọn, dòng doanh thu tháng) nên ba
 * con số cạnh nhau nói về ba khoảng thời gian.
 */
export function useTienKy(ky: string, bat = true) {
  return useQuery<TienNetworkKy, LoiApi>({
    queryKey: KHOA.tienKy(ky),
    queryFn: () => api.get<TienNetworkKy>(`${GOC}/tien-ky?ky=${encodeURIComponent(ky)}`),
    enabled: bat && ky !== '',
  });
}

export function useMotDoiTac(id: string | undefined) {
  return useQuery<Partner, LoiApi>({
    queryKey: KHOA.motDoiTac(id ?? ''),
    queryFn: () => api.get<Partner>(`${GOC}/${id ?? ''}`),
    enabled: Boolean(id),
  });
}

/** `bat = false` cho người không có quyền `networks:view` (màn Quản lý kênh dùng). */
export function useTomTatNetwork(bat = true) {
  return useQuery<NetworksSummary, LoiApi>({
    queryKey: KHOA.tomTat,
    queryFn: () => api.get<NetworksSummary>(`${GOC}/summary`),
    enabled: bat,
  });
}

export function useKenhTheoNetwork(period?: string) {
  return useQuery<PartnerChannelRow[], LoiApi>({
    queryKey: KHOA.kenh(period),
    queryFn: () => api.get<PartnerChannelRow[]>(`${GOC}/kenh${thamSo({ period })}`),
  });
}

export function useKenhCuaDoiTac(id: string | undefined, period?: string) {
  return useQuery<PartnerChannelRow[], LoiApi>({
    queryKey: KHOA.kenhCuaDoiTac(id ?? '', period),
    queryFn: () => api.get<PartnerChannelRow[]>(`${GOC}/${id ?? ''}/kenh${thamSo({ period })}`),
    enabled: Boolean(id),
  });
}

export function useLichSuNetworkCuaKenh(channelId: string | undefined) {
  return useQuery<PartnerChannelRow[], LoiApi>({
    queryKey: KHOA.lichSuKenh(channelId ?? ''),
    queryFn: () => api.get<PartnerChannelRow[]>(`${GOC}/kenh/${channelId ?? ''}/lich-su`),
    enabled: Boolean(channelId),
  });
}

export function useHopDong(partnerId: string | undefined) {
  return useQuery<PartnerContract[], LoiApi>({
    queryKey: KHOA.hopDong(partnerId ?? ''),
    queryFn: () => api.get<PartnerContract[]>(`${GOC}/${partnerId ?? ''}/hop-dong`),
    enabled: Boolean(partnerId),
  });
}

/**
 * **Danh bạ đối tác**: tên mọi đối tác công ty, kể cả đối tác người đang xem
 * không được đọc chi tiết.
 *
 * Chỉ id + tên + loại. Dùng cho ô chọn đối tác lúc gán kênh và cho cảnh báo
 * trùng tên ở form tạo đối tác. Đòi `networks:edit`, nên `bat = false` cho
 * người chỉ có `view`.
 */
export function useDanhBaDoiTac(bat = true) {
  return useQuery<DoiTacDanhBa[], LoiApi>({
    queryKey: KHOA.danhBa,
    queryFn: () => api.get<DoiTacDanhBa[]>(`${GOC}/danh-ba`),
    enabled: bat,
  });
}

/**
 * Điều khoản **chỉ đọc** của hợp đồng một đối tác trong danh bạ (tỷ lệ chia,
 * thuế Mỹ, khoảng ngày). Dùng ở form "Chuyển Network" để chọn đúng bản phủ
 * ngày vào, kể cả với đối tác người đang xem chưa có kênh nào ở đó.
 */
export function useHopDongDanhBa(partnerId: string | undefined) {
  return useQuery<HopDongDanhBa[], LoiApi>({
    queryKey: KHOA.hopDongDanhBa(partnerId ?? ''),
    queryFn: () => api.get<HopDongDanhBa[]>(`${GOC}/danh-ba/${partnerId ?? ''}/hop-dong`),
    enabled: Boolean(partnerId),
  });
}

export function useTemplate(partnerId: string | undefined) {
  return useQuery<StatementImportTemplate[], LoiApi>({
    queryKey: KHOA.template(partnerId ?? ''),
    queryFn: () => api.get<StatementImportTemplate[]>(`${GOC}/${partnerId ?? ''}/template`),
    enabled: Boolean(partnerId),
  });
}

export function useSaoKe(loc: LocSaoKe = {}) {
  return useQuery<Statement[], LoiApi>({
    queryKey: KHOA.saoKe(loc),
    queryFn: () =>
      api.get<Statement[]>(
        `${GOC}/sao-ke${thamSo({ partnerId: loc.partnerId, period: loc.period, status: loc.status })}`,
      ),
  });
}

export function useMotSaoKe(id: string | undefined) {
  return useQuery<Statement & { lines: StatementLine[] }, LoiApi>({
    queryKey: KHOA.motSaoKe(id ?? ''),
    queryFn: () => api.get<Statement & { lines: StatementLine[] }>(`${GOC}/sao-ke/${id ?? ''}`),
    enabled: Boolean(id),
  });
}

/**
 * Danh sách kênh cho ô chọn "Gán kênh".
 * Gọi thẳng endpoint đã ghi trong docs/modules/channels.md, không import code
 * của module khác.
 */
export function useDanhSachKenhChon(batDau = true) {
  return useQuery<{ items: { id: string; name: string; handle: string }[] }, LoiApi>({
    queryKey: ['networks', 'channel-options'],
    queryFn: () =>
      api.get<{ items: { id: string; name: string; handle: string }[] }>(
        '/api/v1/channels?limit=100',
      ),
    enabled: batDau,
  });
}

export function useTicket(status?: TicketStatus) {
  return useQuery<PartnerTicket[], LoiApi>({
    queryKey: KHOA.ticket(status),
    queryFn: () => api.get<PartnerTicket[]>(`${GOC}/ticket${thamSo({ status })}`),
  });
}

/* ------------------------------ Mutation ----------------------------- */

function lamMoiTatCa(qc: ReturnType<typeof useQueryClient>) {
  return qc.invalidateQueries({ queryKey: ['networks'] });
}

/**
 * Làm mới **mọi số tiền** sau khi đổi hợp đồng hoặc link.
 *
 * Tỷ lệ Network và thuế Mỹ của mọi kỳ chưa chốt được tính lại mỗi lần đọc, từ
 * hợp đồng và link — nên sửa một hợp đồng là đổi số ở năm chỗ: màn Network, tab
 * Doanh thu của kênh (`analytics`), bảng doanh thu (`revenue`), cột "Thực nhận"
 * ở Quản lý kênh (`channels`) và Tổng quan (`dashboard`).
 *
 * Trước đây chỉ gọi `['networks']` + `['revenue']`, mà tab Hiệu suất giữ tuổi
 * 5–30 phút (`staleTime` ở `analytics/hieuSuatApi.ts`) nên sau khi sửa thuế vẫn
 * thấy số cũ tới nửa tiếng — đúng lỗi gặp 11/10/2026.
 */
async function lamMoiSoTien(qc: ReturnType<typeof useQueryClient>): Promise<void> {
  await Promise.all([
    qc.invalidateQueries({ queryKey: ['networks'] }),
    qc.invalidateQueries({ queryKey: ['revenue'] }),
    qc.invalidateQueries({ queryKey: ['analytics'] }),
    qc.invalidateQueries({ queryKey: ['channels'] }),
    qc.invalidateQueries({ queryKey: ['dashboard'] }),
  ]);
}

export function useTaoDoiTac() {
  const qc = useQueryClient();
  return useMutation<Partner, LoiApi, CreatePartnerInput>({
    mutationFn: (input) => api.post<Partner>(GOC, input),
    onSuccess: () => lamMoiTatCa(qc),
  });
}

export function useCapNhatDoiTac(id: string) {
  const qc = useQueryClient();
  return useMutation<Partner, LoiApi, UpdatePartnerInput>({
    mutationFn: (input) =>
      goiApi<Partner>(`${GOC}/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    onSuccess: () => lamMoiTatCa(qc),
  });
}

export function useLuuHopDong(partnerId: string) {
  const qc = useQueryClient();
  return useMutation<PartnerContract, LoiApi, ContractInput>({
    mutationFn: (input) => api.post<PartnerContract>(`${GOC}/${partnerId}/hop-dong`, input),
    onSuccess: () => lamMoiSoTien(qc),
  });
}

/** Tình hình hợp đồng: dùng để bật/tắt nút Sửa, Xoá, Kết thúc. */
export function useTinhHinhHopDong(contractId: string | undefined) {
  return useQuery<TinhHinhHopDong, LoiApi>({
    queryKey: ['networks', 'hop-dong', 'tinh-hinh', contractId ?? ''],
    queryFn: () => api.get<TinhHinhHopDong>(`${GOC}/hop-dong/${contractId ?? ''}/tinh-hinh`),
    enabled: Boolean(contractId),
  });
}

export function useNhatKyHopDong(contractId: string | undefined) {
  return useQuery<NhatKyHopDong[], LoiApi>({
    queryKey: ['networks', 'hop-dong', 'nhat-ky', contractId ?? ''],
    queryFn: () => api.get<NhatKyHopDong[]>(`${GOC}/hop-dong/${contractId ?? ''}/nhat-ky`),
    enabled: Boolean(contractId),
  });
}

export function useSuaHopDong(contractId: string) {
  const qc = useQueryClient();
  return useMutation<PartnerContract, LoiApi, CapNhatHopDongInput>({
    mutationFn: (input) => api.patch<PartnerContract>(`${GOC}/hop-dong/${contractId}`, input),
    onSuccess: () => lamMoiSoTien(qc),
  });
}

export function useTaoPhienBanHopDong(contractId: string) {
  const qc = useQueryClient();
  return useMutation<
    { cu: PartnerContract; moi: PartnerContract },
    LoiApi,
    TaoPhienBanHopDongInput
  >({
    mutationFn: (input) =>
      api.post<{ cu: PartnerContract; moi: PartnerContract }>(
        `${GOC}/hop-dong/${contractId}/phien-ban`,
        input,
      ),
    onSuccess: () => lamMoiSoTien(qc),
  });
}

/**
 * Xoá mềm hợp đồng. Bắt nhập lý do; hợp đồng còn kênh gắn thì mỗi kênh phải có
 * một lựa chọn (chuyển hợp đồng khác, hoặc giữ link với tỷ lệ riêng).
 */
export function useXoaHopDong(contractId: string) {
  const qc = useQueryClient();
  return useMutation<{ ok: true; soKenhDaChuyen: number }, LoiApi, XoaHopDongInput>({
    mutationFn: (input) =>
      api.del<{ ok: true; soKenhDaChuyen: number }>(`${GOC}/hop-dong/${contractId}`, input),
    onSuccess: () => lamMoiSoTien(qc),
  });
}

/** Xem trước: xoá hợp đồng thì tỷ lệ Network của các kỳ gần nhất đổi thế nào. */
export function useXemTruocXoaHopDong(contractId: string) {
  return useMutation<XemTruocXoaHopDong, LoiApi, XemTruocXoaHopDongInput>({
    mutationFn: (input) =>
      api.post<XemTruocXoaHopDong>(`${GOC}/hop-dong/${contractId}/xem-truoc-xoa`, input),
  });
}

/** Hợp đồng đã xoá mềm của đối tác. Chỉ `networks:admin` gọi được. */
export function useHopDongDaXoa(partnerId: string | undefined) {
  return useQuery<HopDongDaXoa[], LoiApi>({
    queryKey: ['networks', 'hop-dong', 'da-xoa', partnerId ?? ''],
    queryFn: () => api.get<HopDongDaXoa[]>(`${GOC}/${partnerId ?? ''}/hop-dong/da-xoa`),
    enabled: Boolean(partnerId),
  });
}

/**
 * Khôi phục hợp đồng đã xoá mềm.
 *
 * `ganLaiKenh` gắn lại những kênh bị **tách** lúc xoá (chọn "giữ link, dùng tỷ
 * lệ riêng"). Không gắn lại thì tab Hợp đồng đếm 0 kênh và luật chặn sửa/xoá
 * mất hiệu lực — đúng chỗ sai gặp 11/10/2026.
 */
export function useKhoiPhucHopDong(contractId: string) {
  const qc = useQueryClient();
  return useMutation<KhoiPhucHopDongKetQua, LoiApi, KhoiPhucHopDongInput | void>({
    mutationFn: (input) =>
      api.post<KhoiPhucHopDongKetQua>(`${GOC}/hop-dong/${contractId}/khoi-phuc`, {
        ganLaiKenh: input?.ganLaiKenh ?? false,
      }),
    onSuccess: () => lamMoiSoTien(qc),
  });
}

export interface TepTaiLen {
  fileName: string;
  contentBase64: string;
}

export function useTaiTepHopDong(partnerId: string) {
  return useMutation<UploadFileResult, LoiApi, TepTaiLen>({
    mutationFn: (input) => api.post<UploadFileResult>(`${GOC}/${partnerId}/hop-dong/tep`, input),
  });
}

export function useLuuTemplate(partnerId: string) {
  const qc = useQueryClient();
  return useMutation<StatementImportTemplate, LoiApi, StatementTemplateInput>({
    mutationFn: (input) => api.post<StatementImportTemplate>(`${GOC}/${partnerId}/template`, input),
    onSuccess: () => lamMoiTatCa(qc),
  });
}

export function useGanKenh() {
  const qc = useQueryClient();
  return useMutation<ChannelPartnerLink, LoiApi, AssignChannelInput>({
    mutationFn: (input) => api.post<ChannelPartnerLink>(`${GOC}/gan-kenh`, input),
    onSuccess: () => lamMoiSoTien(qc),
  });
}

/** Sửa ngày hiệu lực / tỷ lệ của một dòng link. */
export function useSuaLink() {
  const qc = useQueryClient();
  return useMutation<ChannelPartnerLink, LoiApi, { linkId: string } & UpdateChannelLinkInput>({
    mutationFn: ({ linkId, ...input }) =>
      api.patch<ChannelPartnerLink>(`${GOC}/kenh/link/${linkId}`, input),
    onSuccess: () => lamMoiSoTien(qc),
  });
}

/** Xoá dòng link rác (ngày vào = ngày rời). */
export function useXoaLink() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, LoiApi, string>({
    mutationFn: (linkId) => api.del<{ ok: true }>(`${GOC}/kenh/link/${linkId}`),
    onSuccess: () => lamMoiSoTien(qc),
  });
}

export function useDanhDauAdsense() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, LoiApi, { channelId: string; status: 'pending' | 'linked' }>({
    mutationFn: ({ channelId, status }) =>
      goiApi<{ ok: true }>(`${GOC}/kenh/${channelId}/adsense`, {
        method: 'PUT',
        body: JSON.stringify({ status }),
      }),
    onSuccess: async () => {
      await lamMoiTatCa(qc);
      await qc.invalidateQueries({ queryKey: ['channels'] });
    },
  });
}

export function useXemTruocSaoKe() {
  return useMutation<StatementPreview, LoiApi, PreviewStatementInput>({
    mutationFn: (input) => api.post<StatementPreview>(`${GOC}/sao-ke/xem-truoc`, input),
  });
}

export function useXacNhanSaoKe() {
  const qc = useQueryClient();
  return useMutation<Statement, LoiApi, ConfirmStatementInput>({
    mutationFn: (input) => api.post<Statement>(`${GOC}/sao-ke`, input),
    onSuccess: () => lamMoiTatCa(qc),
  });
}

export function useDoiTrangThaiSaoKe() {
  const qc = useQueryClient();
  return useMutation<Statement, LoiApi, { id: string; status: StatementStatus }>({
    mutationFn: ({ id, status }) =>
      goiApi<Statement>(`${GOC}/sao-ke/${id}/trang-thai`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      }),
    onSuccess: () => lamMoiTatCa(qc),
  });
}

export function useTaoTicket() {
  const qc = useQueryClient();
  return useMutation<PartnerTicket, LoiApi, CreateTicketInput>({
    mutationFn: (input) => api.post<PartnerTicket>(`${GOC}/ticket`, input),
    onSuccess: () => lamMoiTatCa(qc),
  });
}

export function useDoiTrangThaiTicket() {
  const qc = useQueryClient();
  return useMutation<PartnerTicket, LoiApi, { id: string; status: TicketStatus }>({
    mutationFn: ({ id, status }) =>
      goiApi<PartnerTicket>(`${GOC}/ticket/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      }),
    onSuccess: () => lamMoiTatCa(qc),
  });
}

/** Đọc file người dùng chọn thành base64 để gửi kèm JSON. */
export function docTepThanhBase64(file: File): Promise<string> {
  return new Promise((ok, loi) => {
    const doc = new FileReader();
    doc.onerror = () => {
      loi(new Error('Không đọc được file'));
    };
    doc.onload = () => {
      const ketQua = String(doc.result);
      ok(ketQua.slice(ketQua.indexOf(',') + 1));
    };
    doc.readAsDataURL(file);
  });
}
